<template>
  <div>
    <el-alert v-if="documents && documents.length > 0"
              title="Lưu ý: Kết quả kiểm tra sẽ được lưu trữ trên máy chủ DoIT trong vòng 30 ngày."
              type="warning" show-icon>

    </el-alert>
    <VueGoodTable
      mode="remote"
      @on-page-change="onPageChange"
      @on-sort-change="onSortChange"
      @on-per-page-change="onPerPageChange"
      @on-selected-rows-change="onSelect"
      @on-search="onSearch"
      :columns="columns"
      :rows="documents"
      :totalRows="totalRecords"
      :isLoading.sync="isLoading"
      :search-options="{
        enabled: true,
        placeholder: 'Tìm kiếm',
      }"
      :pagination-options="{
        enabled: true,
        mode: 'records',
        rowsPerPageLabel: 'Số hàng mỗi trang',
        nextLabel: 'Trang sau',
        prevLabel: 'Trang trước',
        ofLabel: 'trong',
      }"
      styleClass="vgt-table striped bordered"
    >
      <div slot="emptystate">Danh sách tài liệu của bạn đang trống</div>
      <template slot="table-row" slot-scope="props">
        <div v-if="props.column.field === 'action'" class="d-flex flex-row justify-content-around">
          <FAIcon
            icon="fas fa-file-download"
            :color="Colors.RAW"
            @click="downloadDocument(props.row, DownloadType.RAW)"
            title="Tải tài liệu gốc"
            :disabled="!documentPermissions.includes(Permission.VIEW)"
          />

          <FAIcon
            icon="fas fa-chart-pie"
            :color="Colors.REPORT"
            @click="$refs[refs.DOCUMENT_REPORT_MODAL].showModal(props.row)"
            title="Tải báo cáo"
            :disabled="!documentPermissions.includes(Permission.REPORT) || props.row.status !== DocumentState.COMPLETE_PROCESS"
          />

          <FAIcon
            icon="fa fa-paper-plane"
            :color="Colors.SEND"
            @click="$refs[refs.DOCUMENT_SEND_MODAL].showModal(props.row)"
            title="Chia sẻ tài liệu"
            :disabled="!documentPermissions.includes(Permission.SHARE)"
          />

          <el-dropdown trigger="hover" :show-timeout="0">
            <div>
              <FAIcon icon="el-icon-more" />
            </div>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item>
                <IconMenuItem
                  @click="$refs[refs.DOCUMENT_INFO_MODAL].showModal(props.row)"
                  icon="fas fa-info-circle"
                  :color="Colors.INFO"
                  title="Thông tin"
                  :disabled="!documentPermissions.includes(Permission.READ) || props.row.status !== DocumentState.COMPLETE_PROCESS"
                />
              </el-dropdown-item>
              <el-dropdown-item>
                <IconMenuItem
                  @click="$refs[refs.DOCUMENT_EDIT_MODAL].showModal(props.row)"
                  icon="fas fa-pen-nib"
                  :color="Colors.EDIT"
                  title="Chỉnh sửa"
                  :disabled="!documentPermissions.includes(Permission.EDIT)"
                />
              </el-dropdown-item>
              <el-dropdown-item>
                <IconMenuItem
                  @click="$refs[refs.DOCUMENT_DELETE_MODAL].showModal(props.row)"
                  icon="fas fa-trash"
                  :color="Colors.DELETE"
                  title="Xóa"
                  :disabled="!documentPermissions.includes(Permission.DELETE) || !DocumentState.DELETABLE_STATES.includes(props.row.status)"
                />
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
        <span v-else-if="props.column.field === 'status'">
          <DocumentStatus :status="props.row.status" />
          <FAIcon
            v-if="props.row.status === DocumentState.ERROR"
            icon="fa fa-redo"
            title="Kiểm tra lại"
            :color="Colors.RETRY"
            @click="$refs[refs.DOCUMENT_CHECK_MODAL].openModal(props.row)"
          />
        </span>
        <span v-else-if="props.column.field === 'title'">
          <CommonLink
            :to="Paths.DOCUMENT_RESULT_VIEW(DocumentTypes.DOCUMENT, props.row.slug)"
            :disable="!documentPermissions.includes(Permission.VIEW) || props.row.status !== DocumentState.COMPLETE_PROCESS"
          >
            {{ props.row.title }}
          </CommonLink>
          <DocumentSharingStatus :document="props.row" @click="$refs[refs.DOCUMENT_SEND_MODAL].showModal(props.row)" />
        </span>
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>
    </VueGoodTable>
    <DocumentInfoModal :ref="refs.DOCUMENT_INFO_MODAL" />
    <DocumentEditModal :ref="refs.DOCUMENT_EDIT_MODAL" @success="loadItems" />
    <DocumentDeleteModal :ref="refs.DOCUMENT_DELETE_MODAL" @success="loadItems" />
    <DocumentSendModal :ref="refs.DOCUMENT_SEND_MODAL" @success="loadItems" />
    <DocumentCheckModal :ref="refs.DOCUMENT_CHECK_MODAL" @success="loadItems" />
    <DocumentReportModal :ref="refs.DOCUMENT_REPORT_MODAL" @success="loadItems" />
  </div>
</template>

<script>
import { VueGoodTable } from 'vue-good-table'
import {
  apiDownloadBatchDocument,
  apiDownloadDocumentAsArrayBuffer,
  apiGetAllDocument,
} from '@/services/document_management_service'
import { ComponentType, DocumentState, DownloadType, Table } from '@/constants/DoITConstant'
import { pushArraybuffer2Client } from '@/helpers/DownloadHelper'
import truncate from 'vue-truncate-collapsed'
import FAIcon from '@/components/common/FAIcon.vue'
import DocumentEditModal from '@/components/DocumentManagement/action-modals/DocumentEditModal.vue'
import DocumentDeleteModal from '@/components/DocumentManagement/action-modals/DocumentDeleteModal.vue'
import DocumentSendModal from '@/components/DocumentManagement/action-modals/DocumentSendModal.vue'
import DocumentInfoModal from '@/components/DocumentManagement/action-modals/DocumentInfoModal.vue'
import DocumentCheckModal from '@/components/DocumentManagement/action-modals/DocumentCheckModal.vue'
import DocumentReportModal from '@/components/DocumentManagement/action-modals/DocumentReportModal.vue'
import { formatDateTime } from '@/helpers/timeHelper.js'
import { Paths } from '@/routes/paths.js'
import { Colors, DocumentTypes, Permission } from '@/constants/DoITConstant.js'
import DocumentStatus from '@/components/common/document/DocumentStatus.vue'
import CommonLink from '@/components/common/CommonLink.vue'
import DocumentSharingStatus from '@/components/common/document/DocumentSharingStatus.vue'
import { mapGetters } from 'vuex'
import { AuthenticateGetter } from '@/store/modules/AuthenticateStore.js'
import IconMenuItem from '@/components/common/IconMenuItem.vue'

export default {
  name: 'DocumentList',
  components: {
    IconMenuItem,
    DocumentSharingStatus,
    DocumentStatus,
    DocumentReportModal,
    DocumentCheckModal,
    DocumentInfoModal,
    DocumentSendModal,
    DocumentDeleteModal,
    DocumentEditModal,
    VueGoodTable,
    truncate,
    FAIcon,
    CommonLink,
  },
  props: {},
  data: function () {
    return {
      columns: [],
      componentType: ComponentType.DOCUMENT,
      DownloadType: DownloadType,
      totalRecords: 0,
      DocumentState,
      serverParams: {
        sortAscending: false,
        sortField: 'uploadTime',
        page: 0,
        size: 10,
      },
      selectedRowSlugs: new Set(),
      documents: [],
      selectedRows: [],
      isShowingDropCreator: true,
      interval: null,
      isLoading: true,
      Permission,
      refs: {
        DOCUMENT_INFO_MODAL: 'documentInfoModal',
        DOCUMENT_EDIT_MODAL: 'documentEditModal',
        DOCUMENT_DELETE_MODAL: 'documentDeleteModal',
        DOCUMENT_SEND_MODAL: 'documentSendModal',
        DOCUMENT_CHECK_MODAL: 'documentCheckModal',
        DOCUMENT_REPORT_MODAL: 'documentReportModal',
      },
      Paths,
      DocumentTypes,
      Colors,
    }
  },
  mounted() {
    this.columns = [
      {
        label: 'ID',
        field: 'id',
      },
      {
        label: 'Tiêu đề',
        field: 'title',
      },
      {
        label: 'Độ trùng lặp',
        field: 'score',
        formatFn: (score) => (score !== null ? `${score} %` : ''),
        type: 'string',
      },
      {
        label: 'Lỗi chính tả',
        field: 'spellError',
      },
      {
        label: 'Ngày tải lên',
        field: 'uploadTime',
        formatFn: formatDateTime,
      },
      {
        label: 'Trạng thái',
        field: 'status',
      },
      {
        label: 'Hành động',
        field: 'action',
        width: '120px',
      },
    ]
    this.loadItems().then((totalDocument) => {
      if (totalDocument == 0) {
        this.$emit('empty')
      }
    })
    this.interval = setInterval(() => {
      if (this.selectedRows.length === 0) {
        this.loadItems()
      }
    }, Table.RELOAD_INTERVAL)
  },
  destroyed() {
    clearInterval(this.interval)
  },
  computed: {
    ...mapGetters({
      documentPermissions: AuthenticateGetter.DOCUMENT_PERMISSIONS,
    }),
  },
  methods: {
    async downloadDocument(document, type) {
      const loading = this.$loading({
        lock: true,
        text: 'Đang tải tài liệu',
        spinner: 'el-icon-loading',
      })
      let res = await apiDownloadDocumentAsArrayBuffer(document.slug, type)
      pushArraybuffer2Client(res, document.name)
      loading.close()
    },
    onSearch(params) {
      this.serverParams.searchKey = params.searchTerm
      this.loadItems()
    },

    onPageChange(params) {
      this.serverParams.page = params.currentPage - 1
      this.loadItems()
    },

    onPerPageChange(params) {
      this.serverParams.size = params.currentPerPage
      this.loadItems()
    },

    onSortChange(params) {
      this.serverParams.sortAscending = params[0].type === 'asc'
      this.serverParams.sortField = params[0].field
      this.serverParams.page = 0
      this.loadItems()
    },

    onSelect(params) {
      this.selectedRowSlugs = new Set()
      params.selectedRows.forEach((row) => this.selectedRowSlugs.add(row.slug))
      this.selectedRows = [...params.selectedRows]
    },
    async downloadBatch(type) {
      if (this.selectedRowSlugs.size === 0) return
      const loading = this.$loading({
        lock: true,
        text: 'Đang tải tài liệu',
        spinner: 'el-icon-loading',
      })
      let res = await apiDownloadBatchDocument(this.selectedRowSlugs, type)
      pushArraybuffer2Client(res, 'batch_' + type + '.zip')
      loading.close()
    },
    async loadItems() {
      let responseAllDocument = await apiGetAllDocument(this.serverParams)
      this.documents = responseAllDocument.data
      this.totalRecords = responseAllDocument.meta.total
      return responseAllDocument.meta.total
    },
  },
}
</script>
